import * as THREE from 'three';

export interface SurfaceRect {
  A: THREE.Vector3;
  B: THREE.Vector3;
  C: THREE.Vector3;
  D: THREE.Vector3;
  uAxis: THREE.Vector3;
  vAxis: THREE.Vector3;
  normal: THREE.Vector3;
  width: number;
  height: number;
  origin: THREE.Vector3;
}

const COLLINEAR_EPS = 1e-6;
const HEIGHT_EPS = 1e-6;

export const rectFromThreeCorners = (
  p1: THREE.Vector3,
  p2: THREE.Vector3,
  p3: THREE.Vector3,
): SurfaceRect | null => {
  const ab = new THREE.Vector3().subVectors(p2, p1);
  const ac = new THREE.Vector3().subVectors(p3, p1);
  const width = ab.length();
  if (width < COLLINEAR_EPS) return null;

  const normal = new THREE.Vector3().crossVectors(ab, ac);
  if (normal.length() < COLLINEAR_EPS) return null;
  normal.normalize();

  const uAxis = ab.clone().normalize();
  const vAxis = new THREE.Vector3().crossVectors(normal, uAxis).normalize();

  const h = new THREE.Vector3().subVectors(p3, p2).dot(vAxis);
  if (Math.abs(h) < HEIGHT_EPS) return null;

  const A = p1.clone();
  const B = p2.clone();
  const C = p2.clone().addScaledVector(vAxis, h);
  const D = p1.clone().addScaledVector(vAxis, h);

  return {
    A,
    B,
    C,
    D,
    uAxis,
    vAxis,
    normal,
    width,
    height: Math.abs(h),
    origin: A,
  };
};

export const rectFromBasis = (
  origin: THREE.Vector3,
  uAxis: THREE.Vector3,
  vAxis: THREE.Vector3,
  width: number,
  height: number,
): SurfaceRect => {
  const u = uAxis.clone().normalize();
  const v = vAxis.clone().normalize();
  const A = origin.clone();
  const B = origin.clone().addScaledVector(u, width);
  const C = B.clone().addScaledVector(v, height);
  const D = A.clone().addScaledVector(v, height);
  const normal = new THREE.Vector3().crossVectors(u, v).normalize();
  return { A, B, C, D, uAxis: u, vAxis: v, normal, width, height, origin: A };
};

export const rectFromCorners = (
  A: THREE.Vector3,
  B: THREE.Vector3,
  D: THREE.Vector3,
): SurfaceRect | null => {
  const ab = new THREE.Vector3().subVectors(B, A);
  const ad = new THREE.Vector3().subVectors(D, A);
  const width = ab.length();
  const height = ad.length();
  if (width < COLLINEAR_EPS || height < COLLINEAR_EPS) return null;
  const u = ab.clone().normalize();
  const v = ad.clone().normalize();
  const C = B.clone().addScaledVector(v, height);
  const normal = new THREE.Vector3().crossVectors(u, v).normalize();
  return { A: A.clone(), B: B.clone(), C, D: D.clone(), uAxis: u, vAxis: v, normal, width, height, origin: A.clone() };
};

// Build a perfectly vertical rectangle ("wall") from 3 picked points:
//   - A is anchored at p1.
//   - B is p2 with its Z snapped to p1.Z (AB lies on a horizontal line).
//   - The v-axis is forced to world up (or down, depending on the sign of click 3's
//     Z relative to p1) so the rectangle's plane is perpendicular to the ground.
//   - height = |p3.z - p1.z|. Click 3's XY is ignored.
// Returns null on degenerate input (collinear horizontal AB or zero height).
export const rectFromVerticalCorners = (
  p1: THREE.Vector3,
  p2: THREE.Vector3,
  p3: THREE.Vector3,
): SurfaceRect | null => {
  const A = p1.clone();
  const B = new THREE.Vector3(p2.x, p2.y, p1.z);
  if (B.clone().sub(A).length() < 1e-6) return null;

  const dz = p3.z - p1.z;
  if (Math.abs(dz) < 1e-6) return null;

  const D = new THREE.Vector3(A.x, A.y, A.z + dz);
  return rectFromCorners(A, B, D);
};

export const rectFromPolygonCoords = (
  coords: [number, number, number][],
): SurfaceRect | null => {
  if (coords.length < 4) return null;
  const A = new THREE.Vector3(...coords[0]);
  const B = new THREE.Vector3(...coords[1]);
  const D = new THREE.Vector3(...coords[3]);
  return rectFromCorners(A, B, D);
};

/**
 * Diagonalise a 3×3 symmetric matrix via Jacobi rotations. Returns
 * eigenvalues sorted ascending and the corresponding eigenvectors as
 * `THREE.Vector3` columns. Used by `rectFromManyPicks` to find the
 * best-fit plane through an arbitrary point set; not exported to keep
 * the public surface small.
 *
 * Algorithm: classical Jacobi sweep — at each step, find the largest
 * off-diagonal element, apply a Givens rotation that zeroes it, accumulate
 * the rotation into the eigenvector matrix. Converges quadratically near
 * the solution; for 3×3 the loop almost always exits in <10 iterations.
 */
const jacobiEigen3 = (
  m: [[number, number, number], [number, number, number], [number, number, number]],
): { values: [number, number, number]; vectors: [THREE.Vector3, THREE.Vector3, THREE.Vector3] } => {
  // Working copy of the matrix; gets diagonalised in place.
  const a: number[][] = m.map((row) => row.slice());
  // Accumulator for the rotation product — starts as identity. Columns end
  // up holding the eigenvectors corresponding to A's diagonal.
  const v: number[][] = [
    [1, 0, 0],
    [0, 1, 0],
    [0, 0, 1],
  ];

  const MAX_ITER = 50;
  for (let iter = 0; iter < MAX_ITER; iter++) {
    // Find the largest off-diagonal magnitude.
    let p = 0;
    let q = 1;
    let max = Math.abs(a[0][1]);
    if (Math.abs(a[0][2]) > max) {
      p = 0;
      q = 2;
      max = Math.abs(a[0][2]);
    }
    if (Math.abs(a[1][2]) > max) {
      p = 1;
      q = 2;
      max = Math.abs(a[1][2]);
    }
    if (max < 1e-14) break;

    // Compute the rotation angle that zeroes a[p][q].
    const apq = a[p][q];
    const app = a[p][p];
    const aqq = a[q][q];
    const theta = (aqq - app) / (2 * apq);
    let t: number;
    if (Math.abs(theta) > 1e10) {
      t = 1 / (2 * theta);
    } else {
      t = (theta >= 0 ? 1 : -1) / (Math.abs(theta) + Math.sqrt(theta * theta + 1));
    }
    const cosT = 1 / Math.sqrt(t * t + 1);
    const sinT = t * cosT;

    // Apply the rotation to A.
    a[p][p] = app - t * apq;
    a[q][q] = aqq + t * apq;
    a[p][q] = 0;
    a[q][p] = 0;
    for (let i = 0; i < 3; i++) {
      if (i !== p && i !== q) {
        const aip = a[i][p];
        const aiq = a[i][q];
        a[i][p] = cosT * aip - sinT * aiq;
        a[p][i] = a[i][p];
        a[i][q] = sinT * aip + cosT * aiq;
        a[q][i] = a[i][q];
      }
      const vip = v[i][p];
      const viq = v[i][q];
      v[i][p] = cosT * vip - sinT * viq;
      v[i][q] = sinT * vip + cosT * viq;
    }
  }

  const eigenvalues: [number, number, number] = [a[0][0], a[1][1], a[2][2]];
  const eigenvectors: [THREE.Vector3, THREE.Vector3, THREE.Vector3] = [
    new THREE.Vector3(v[0][0], v[1][0], v[2][0]),
    new THREE.Vector3(v[0][1], v[1][1], v[2][1]),
    new THREE.Vector3(v[0][2], v[1][2], v[2][2]),
  ];

  // Sort ascending so caller can pick eigenvectors[0] as the plane normal
  // (smallest variance, perpendicular to the plane) and the other two as
  // in-plane axes.
  const order = [0, 1, 2].sort((x, y) => eigenvalues[x] - eigenvalues[y]) as [0 | 1 | 2, 0 | 1 | 2, 0 | 1 | 2];
  return {
    values: [eigenvalues[order[0]], eigenvalues[order[1]], eigenvalues[order[2]]],
    vectors: [eigenvectors[order[0]], eigenvectors[order[1]], eigenvectors[order[2]]],
  };
};

/**
 * Build a rectangle from N≥3 picks on a (roughly) flat surface. The user
 * clicks any number of points on the actual surface and we return the
 * tightest axis-aligned-in-fitted-basis rectangle that contains them all.
 *
 * For ≥4 picks the plane is fitted by **PCA over all picks** — covariance
 * matrix of centred picks → Jacobi eigendecomposition → smallest-eigenvalue
 * eigenvector is the plane normal, largest two span the plane. This is
 * what the user expects from "many points" picking: every pick has equal
 * voting weight, so 10 ground picks dominate 2 wall picks instead of the
 * first three picks setting the orientation.
 *
 * Lock semantics in the ≥4 case (different from the 3-pick legacy path,
 * because the locks here constrain the *fitted* plane, not the AB edge of
 * a 3-corner build):
 *   - **verticalLock**: vAxis is forced to world-up. uAxis = the PCA
 *     in-plane axis with the larger horizontal component, projected to
 *     XY. Result: a vertical wall whose horizontal direction is whichever
 *     direction the picks span the most in plan view.
 *   - **horizontalLock**: uAxis is forced into the world XY plane (no Z
 *     component) — same projection trick. vAxis = normal × uAxis with the
 *     positive-Z choice so the rectangle goes "up". The plane can still
 *     tilt around uAxis based on the PCA fit.
 *   - **No lock**: PCA orientation used as-is.
 *
 * For exactly 3 picks we fall back to the legacy 3-corner logic so users
 * who finalize after 3 picks get the same rectangle as the 3-pick flow.
 *
 * Returns null on degenerate input (collinear/coincident picks, zero-extent
 * bounding box, lock projection that collapses to a point).
 */
export const rectFromManyPicks = (
  picks: THREE.Vector3[],
  options: { horizontalLock: boolean; verticalLock: boolean } = {
    horizontalLock: false,
    verticalLock: false,
  },
): SurfaceRect | null => {
  if (picks.length < 3) return null;

  // 3-pick parity path: same logic as the legacy 3-pick flow so users who
  // Finalize at exactly 3 picks get an identical rectangle.
  if (picks.length === 3) {
    const [p1, p2Raw, p3] = picks;
    if (options.verticalLock) return rectFromVerticalCorners(p1, p2Raw, p3);
    const p2 = options.horizontalLock ? new THREE.Vector3(p2Raw.x, p2Raw.y, p1.z) : p2Raw;
    return rectFromThreeCorners(p1, p2, p3);
  }

  // Step 1: PCA for plane orientation.
  const centroid = new THREE.Vector3();
  for (const p of picks) centroid.add(p);
  centroid.multiplyScalar(1 / picks.length);

  let cxx = 0;
  let cyy = 0;
  let czz = 0;
  let cxy = 0;
  let cxz = 0;
  let cyz = 0;
  for (const p of picks) {
    const dx = p.x - centroid.x;
    const dy = p.y - centroid.y;
    const dz = p.z - centroid.z;
    cxx += dx * dx;
    cyy += dy * dy;
    czz += dz * dz;
    cxy += dx * dy;
    cxz += dx * dz;
    cyz += dy * dz;
  }
  const eig = jacobiEigen3([
    [cxx, cxy, cxz],
    [cxy, cyy, cyz],
    [cxz, cyz, czz],
  ]);
  // values ascending → vectors[0] = smallest variance (plane normal),
  // vectors[2] = largest variance (in-plane primary), vectors[1] = secondary.
  let normal = eig.vectors[0].clone().normalize();
  let uAxis = eig.vectors[2].clone().normalize();
  let vAxis = eig.vectors[1].clone().normalize();

  // Step 2: apply lock constraints.
  if (options.verticalLock) {
    // Force vertical plane: vAxis is world up. uAxis is the most-horizontal
    // PCA in-plane direction, flattened to XY.
    const uPrimary = new THREE.Vector3(uAxis.x, uAxis.y, 0);
    const uSecondary = new THREE.Vector3(vAxis.x, vAxis.y, 0);
    const horizontalU = uPrimary.length() >= uSecondary.length() ? uPrimary : uSecondary;
    if (horizontalU.length() < COLLINEAR_EPS) return null;
    horizontalU.normalize();
    uAxis = horizontalU;
    vAxis = new THREE.Vector3(0, 0, 1);
    normal = new THREE.Vector3().crossVectors(uAxis, vAxis).normalize();
  } else if (options.horizontalLock) {
    // Force uAxis into the XY plane. Same projection trick as verticalLock,
    // but vAxis is derived from the PCA normal so the plane is free to tilt
    // around the horizontal AB.
    const uPrimary = new THREE.Vector3(uAxis.x, uAxis.y, 0);
    const uSecondary = new THREE.Vector3(vAxis.x, vAxis.y, 0);
    const horizontalU = uPrimary.length() >= uSecondary.length() ? uPrimary : uSecondary;
    if (horizontalU.length() < COLLINEAR_EPS) return null;
    horizontalU.normalize();
    uAxis = horizontalU;
    let newVAxis = new THREE.Vector3().crossVectors(normal, uAxis);
    if (newVAxis.length() < COLLINEAR_EPS) {
      // PCA normal nearly parallel to chosen horizontal uAxis — fall back
      // to world-up so we still produce a rectangle.
      newVAxis = new THREE.Vector3(0, 0, 1);
    }
    newVAxis.normalize();
    if (newVAxis.z < 0) newVAxis.negate(); // rectangle should go "up"
    vAxis = newVAxis;
    normal = new THREE.Vector3().crossVectors(uAxis, vAxis).normalize();
  }

  // Step 3: bounding box of all picks in (uAxis, vAxis) basis, anchored at
  // the picks' centroid (any point in the plane works; centroid keeps the
  // rectangle visually balanced over the picks).
  let uMin = Infinity;
  let uMax = -Infinity;
  let vMin = Infinity;
  let vMax = -Infinity;
  for (const pick of picks) {
    const rel = pick.clone().sub(centroid);
    const u = rel.dot(uAxis);
    const v = rel.dot(vAxis);
    if (u < uMin) uMin = u;
    if (u > uMax) uMax = u;
    if (v < vMin) vMin = v;
    if (v > vMax) vMax = v;
  }

  const width = uMax - uMin;
  const height = vMax - vMin;
  if (width < COLLINEAR_EPS || height < HEIGHT_EPS) return null;

  // Step 4: build the rectangle from the fitted basis + bounds. Origin is
  // centroid + uMin*uAxis + vMin*vAxis so the rectangle covers all picks.
  const origin = centroid
    .clone()
    .addScaledVector(uAxis, uMin)
    .addScaledVector(vAxis, vMin);
  return rectFromBasis(origin, uAxis, vAxis, width, height);
};

export const rectToPolygonCoords = (rect: SurfaceRect): [number, number, number][] => [
  [rect.A.x, rect.A.y, rect.A.z],
  [rect.B.x, rect.B.y, rect.B.z],
  [rect.C.x, rect.C.y, rect.C.z],
  [rect.D.x, rect.D.y, rect.D.z],
  [rect.A.x, rect.A.y, rect.A.z],
];

export const rectCenter = (rect: SurfaceRect): THREE.Vector3 =>
  rect.A.clone().add(rect.B).add(rect.C).add(rect.D).multiplyScalar(0.25);

export const rectEdgeMidpoints = (rect: SurfaceRect) => ({
  AB: rect.A.clone().add(rect.B).multiplyScalar(0.5),
  BC: rect.B.clone().add(rect.C).multiplyScalar(0.5),
  CD: rect.C.clone().add(rect.D).multiplyScalar(0.5),
  DA: rect.D.clone().add(rect.A).multiplyScalar(0.5),
});

export const projectOntoPlane = (
  point: THREE.Vector3,
  origin: THREE.Vector3,
  normal: THREE.Vector3,
): THREE.Vector3 => {
  const d = new THREE.Vector3().subVectors(point, origin).dot(normal);
  return point.clone().addScaledVector(normal, -d);
};

// Closest point on the line (anchor + t * dir) to the ray (origin + s * direction).
// Used for the depth/normal-axis drag handle so the surface translates along its
// normal in response to the mouse ray. Returns null when the ray is parallel to
// the line (no unique closest point).
export const projectRayOntoLine = (
  rayOrigin: THREE.Vector3,
  rayDirection: THREE.Vector3,
  lineAnchor: THREE.Vector3,
  lineDirection: THREE.Vector3,
): THREE.Vector3 | null => {
  const r = rayDirection.clone().normalize();
  const l = lineDirection.clone().normalize();
  const w0 = rayOrigin.clone().sub(lineAnchor);
  const b = r.dot(l);
  const denom = 1 - b * b;
  if (Math.abs(denom) < 1e-9) return null;
  const d = r.dot(w0);
  const e = l.dot(w0);
  const t = (e - b * d) / denom;
  return lineAnchor.clone().addScaledVector(l, t);
};

export type SurfaceHandle =
  | 'corner-A'
  | 'corner-B'
  | 'corner-C'
  | 'corner-D'
  | 'edge-AB'
  | 'edge-BC'
  | 'edge-CD'
  | 'edge-DA'
  | 'center'
  | 'normal';

type Local = { u: number; v: number };

const localOf = (rect: SurfaceRect, p: THREE.Vector3): Local => ({
  u: p.clone().sub(rect.origin).dot(rect.uAxis),
  v: p.clone().sub(rect.origin).dot(rect.vAxis),
});

const localToWorld = (rect: SurfaceRect, l: Local): THREE.Vector3 =>
  rect.origin.clone().addScaledVector(rect.uAxis, l.u).addScaledVector(rect.vAxis, l.v);

export const applyHandleDrag = (
  rect: SurfaceRect,
  handle: SurfaceHandle,
  newWorldPos: THREE.Vector3,
): SurfaceRect | null => {
  // Normal-axis drag — translate the entire rectangle along its normal so it
  // moves in/out of its own plane. newWorldPos is expected to be a point on
  // the normal line through the current center (caller is responsible for
  // projecting the mouse ray onto that line).
  if (handle === 'normal') {
    const oldCenter = rect.A.clone().add(rect.B).add(rect.C).add(rect.D).multiplyScalar(0.25);
    const delta = newWorldPos.clone().sub(oldCenter).dot(rect.normal);
    const offset = rect.normal.clone().multiplyScalar(delta);
    return rectFromCorners(
      rect.A.clone().add(offset),
      rect.B.clone().add(offset),
      rect.D.clone().add(offset),
    );
  }

  const aL: Local = { u: 0, v: 0 };
  const bL: Local = { u: rect.width, v: 0 };
  const cL: Local = { u: rect.width, v: rect.height };
  const dL: Local = { u: 0, v: rect.height };
  const np = localOf(rect, newWorldPos);

  let newA = aL;
  let newB = bL;
  let newD = dL;

  switch (handle) {
    case 'corner-A':
      newA = np;
      newB = { u: cL.u, v: np.v };
      newD = { u: np.u, v: cL.v };
      break;
    case 'corner-B':
      newB = np;
      newA = { u: dL.u, v: np.v };
      // newC = { u: np.u, v: dL.v } — derived from rectFromCorners
      break;
    case 'corner-C':
      // newC = np
      newB = { u: np.u, v: aL.v };
      newD = { u: aL.u, v: np.v };
      newA = aL;
      break;
    case 'corner-D':
      newD = np;
      newA = { u: np.u, v: bL.v };
      newB = { u: bL.u, v: bL.v };
      break;
    case 'edge-AB':
      newA = { u: aL.u, v: np.v };
      newB = { u: bL.u, v: np.v };
      newD = dL;
      break;
    case 'edge-BC':
      newB = { u: np.u, v: bL.v };
      newA = aL;
      newD = dL;
      break;
    case 'edge-CD':
      newD = { u: dL.u, v: np.v };
      newA = aL;
      newB = bL;
      break;
    case 'edge-DA':
      newA = { u: np.u, v: aL.v };
      newD = { u: np.u, v: dL.v };
      newB = bL;
      break;
    case 'center': {
      const dx = np.u - rect.width / 2;
      const dy = np.v - rect.height / 2;
      newA = { u: aL.u + dx, v: aL.v + dy };
      newB = { u: bL.u + dx, v: bL.v + dy };
      newD = { u: dL.u + dx, v: dL.v + dy };
      break;
    }
  }

  return rectFromCorners(localToWorld(rect, newA), localToWorld(rect, newB), localToWorld(rect, newD));
};
